import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Per-user bookmarks of Cosmos pages — mirrors server/bookmarks.ts. */

export interface Bookmark { id: string; title: string; path: string; position: number; createdAt: string }
type List = { bookmarks: Bookmark[] };

const KEY = ['bookmarks'];

export function useBookmarks() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<List>('/api/bookmarks'), staleTime: 5 * 60_000 });
}

/** Every mutation returns the new list, so the cache is replaced rather than refetched. */
function useListMutation<V>(fn: (v: V) => Promise<List>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (r) => qc.setQueryData(KEY, r) });
}

export const useAddBookmark = () => useListMutation((b: { title: string; path: string }) => api.post<List>('/api/bookmarks', b));
export const useRenameBookmark = () => useListMutation(({ id, title }: { id: string; title: string }) => api.patch<List>(`/api/bookmarks/${id}`, { title }));
export const useRemoveBookmark = () => useListMutation((id: string) => api.del<List>(`/api/bookmarks/${id}`));
export const useReorderBookmarks = () => useListMutation((ids: string[]) => api.put<List>('/api/bookmarks/order', { ids }));

/** The path a bookmark stores for the current location (path + query, no hash). */
export const currentBookmarkPath = (loc: { pathname: string; search: string }) => `${loc.pathname}${loc.search}`;
